import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Grid, Paper, Typography, Button, CircularProgress, Alert, Stack, Chip,
  TextField, InputAdornment, List, ListItemButton, ListItemText, Checkbox,
} from '@mui/material';
import {
  Print, PictureAsPdf, GridOn, Refresh, Search, Receipt, Layers,
  Category as CategoryIcon,
} from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportReceiptsLedgerURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtDateShort = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
};

const METHOD_LABEL = { cash: 'Cash', cheque: 'Cheque', transfer: 'Transfer', mobile_money: 'Mobile Money' };

const NAVY  = '#0B2545';
const GREEN = '#0F766E';
const RED   = '#B91C1C';
const RULE  = '#0B2545';

export default function ReceiptsLedger({ start, end, groupBy = 'type' }) {
  const groupField = groupBy === 'category' ? 'income_category' : 'income_type_name';
  const groupTitle = groupBy === 'category' ? 'INCOME CATEGORIES' : 'INCOME TYPES';
  const groupSingular = groupBy === 'category' ? 'Category' : 'Income Type';
  const groupPlural   = groupBy === 'category' ? 'Categories' : 'Income Types';
  const GroupIcon = groupBy === 'category' ? CategoryIcon : Layers;
  const reportTitle = groupBy === 'category' ? 'RECEIPTS LEDGER — BY CATEGORY' : 'RECEIPTS LEDGER';

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [selected, setSelected] = useState(new Set()); // empty = All
  const [search, setSearch]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportReceiptsLedgerURL, {
      headers: headers(),
      params: { start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); setSelected(new Set()); setSearch(''); /* eslint-disable-next-line */ }, [start, end, groupBy]);

  // Groups derived from receipts by the chosen field
  const groups = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const r of data.receipts) {
      const key = r[groupField] || '—';
      if (!map.has(key)) map.set(key, { name: key, count: 0, total: 0 });
      const g = map.get(key);
      g.count += 1;
      g.total += Number(r.amount || 0);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data, groupField]);

  const toggleGroup = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set());
  const selectEveryOne = () => setSelected(new Set(groups.map(g => g.name)));

  // Client-side filter: multi-group + search
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.receipts.filter(r => {
      if (selected.size > 0 && !selected.has(r[groupField] || '—')) return false;
      if (!q) return true;
      return [
        r.receipt_no, r.income_type_name, r.income_category, r.narration,
        r.cheque_no, r.recorded_by,
      ].some(v => (v || '').toString().toLowerCase().includes(q));
    });
  }, [data, selected, search, groupField]);

  const filteredTotal = useMemo(
    () => filtered.reduce((s, r) => s + Number(r.amount || 0), 0),
    [filtered]
  );

  const filterLabel = selected.size === 0
    ? null
    : (selected.size <= 3
        ? [...selected].join(', ')
        : `${selected.size} ${groupPlural.toLowerCase()} selected`);

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1200,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Receipts Ledger - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        .filter-line { margin-top: 4px; font-size: 11px; color: #444; }
        .filter-line b { color: ${NAVY}; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        thead th { text-align: left; font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 8px 10px; }
        thead th.right { text-align: right; }
        tbody td { padding: 6px 10px; font-size: 12px; border-bottom: 1px solid #eef0f4; }
        tbody tr:nth-child(even) td { background: #fafbfc; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .grand td { border-top: 3px double ${NAVY}; border-bottom: 3px double ${NAVY}; font-weight: 800; font-size: 13px; background: #fffdf5; padding: 12px 10px; text-transform: uppercase; letter-spacing: 0.08em; color: ${GREEN}; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4 landscape; margin: 12mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 36;

    // Letterhead
    let y = 32;
    const logoSize = 52;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 12;
      } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 32);
    y += Math.max(logoSize, 36) + 6;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.0);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 16;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(11, 37, 69);
    const title = reportTitle;
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.6);
    doc.line(tx - 16, y - 4, tx + titleW + 16, y - 4);
    doc.text(title, pageW / 2, y + 10, { align: 'center' });
    doc.line(tx - 16, y + 15, tx + titleW + 15, y + 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    doc.text(
      `For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 30, { align: 'center' }
    );
    if (filterLabel) {
      doc.setFontSize(8); doc.text(`Filtered by ${groupSingular}: ${filterLabel}`, pageW / 2, y + 42, { align: 'center' });
      y += 8;
    }
    y += 40;

    // Table
    const body = filtered.map((r, i) => ([
      String(i + 1),
      fmtDateShort(r.income_date),
      r.receipt_no || '—',
      r.income_type_name || '—',
      r.income_category || '—',
      r.narration || '—',
      METHOD_LABEL[r.payment_method] || r.payment_method || '—',
      { content: fmt(r.amount), styles: { halign: 'right' } },
    ]));
    body.push([
      { content: `TOTAL (${filtered.length} receipt${filtered.length === 1 ? '' : 's'})`, colSpan: 7,
        styles: { fontStyle: 'bold', fillColor: [255, 253, 245], textColor: [15, 118, 110], fontSize: 11 } },
      { content: fmt(filteredTotal), styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 253, 245], textColor: [15, 118, 110], fontSize: 11 } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [[
        '#',
        'Date',
        'Receipt No.',
        'Income Type',
        'Category',
        'Description',
        'Method',
        { content: 'Amount (GH₵)', styles: { halign: 'right' } },
      ]],
      body,
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 4, lineColor: [230, 232, 238], lineWidth: 0.4 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle' },
      alternateRowStyles: { fillColor: [250, 251, 252] },
      columnStyles: {
        0: { cellWidth: 26, halign: 'center' },
        1: { cellWidth: 70 },
        2: { cellWidth: 70 },
        3: { cellWidth: 100 },
        4: { cellWidth: 90 },
        5: { cellWidth: 'auto' },
        6: { cellWidth: 70 },
        7: { cellWidth: 80, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin, bottom: 60 },
      didDrawPage: () => {
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 18, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 18);
      },
    });

    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    doc.save(`Receipts_Ledger_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const aoa = [];
    aoa.push([data.church?.name || 'Church']);
    if (data.church?.address) aoa.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) aoa.push([contact]);
    aoa.push([]);
    aoa.push([reportTitle]);
    aoa.push([`For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    if (filterLabel) aoa.push([`Filtered by ${groupSingular}: ${filterLabel}`]);
    aoa.push([]);
    aoa.push(['#', 'Date', 'Receipt No.', 'Income Type', 'Category', 'Description', 'Method', 'Cheque No.', 'Recorded By', 'Amount (GH₵)']);

    filtered.forEach((r, i) => {
      aoa.push([
        i + 1,
        r.income_date || '',
        r.receipt_no || '',
        r.income_type_name || '',
        r.income_category || '',
        r.narration || '',
        METHOD_LABEL[r.payment_method] || r.payment_method || '',
        r.cheque_no || '',
        r.recorded_by || '',
        Number(r.amount || 0),
      ]);
    });
    aoa.push([]);
    aoa.push(['', '', '', '', '', '', '', '', `TOTAL (${filtered.length} receipts)`, Number(filteredTotal)]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 5 }, { wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 18 },
      { wch: 40 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 },
    ];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: 9 });
      const cell = ws[addr];
      if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Receipts Ledger');
    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    XLSX.writeFile(wb, `Receipts_Ledger_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading receipts for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  return (
    <Box>
      {/* Search + action bar (full width) */}
      <Paper sx={{ p: 2, mb: 2, border: '1px solid #E2E8F0', borderRadius: 2 }} elevation={0}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            size="small" placeholder="Search receipt no., type, description…"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ width: { xs: '100%', md: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }} flexWrap="wrap">
            <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
            <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint} disabled={filtered.length === 0}>Print</Button>
            <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel} disabled={filtered.length === 0}
              sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
              Excel
            </Button>
            <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf} disabled={filtered.length === 0}
              sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
              PDF
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Two-column layout: left = income type list, right = report paper */}
      <Grid container spacing={2}>
        {/* Left column: Income type list */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper sx={{
            border: '1px solid #E2E8F0', borderRadius: 2,
            position: { md: 'sticky' }, top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 200px)' },
            overflow: 'auto',
          }} elevation={0}>
            <Box sx={{
              px: 2, py: 1.5, bgcolor: NAVY, color: '#fff',
              display: 'flex', alignItems: 'center', gap: 1,
              borderRadius: '8px 8px 0 0',
            }}>
              <GroupIcon sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: '0.06em' }}>
                {groupTitle}
              </Typography>
            </Box>

            {/* Selection controls */}
            <Box sx={{
              px: 1.5, py: 1, borderBottom: '1px solid #eef0f4',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              bgcolor: '#f8fafc',
            }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: NAVY }}>
                {selected.size === 0
                  ? `All shown (${data.receipts.length})`
                  : `${selected.size} selected · ${filtered.length} match`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Button size="small" onClick={selectEveryOne}
                  sx={{ minWidth: 0, px: 1, fontSize: 11, textTransform: 'none' }}
                  disabled={groups.length === 0}>
                  All
                </Button>
                <Button size="small" onClick={selectAll}
                  sx={{ minWidth: 0, px: 1, fontSize: 11, textTransform: 'none' }}
                  disabled={selected.size === 0}>
                  Clear
                </Button>
              </Stack>
            </Box>

            <List dense disablePadding>
              <ListItemButton
                selected={selected.size === 0}
                onClick={selectAll}
                sx={{
                  borderBottom: '1px solid #eef0f4',
                  '&.Mui-selected': { bgcolor: `${NAVY}15`, borderLeft: `4px solid ${NAVY}` },
                  '&.Mui-selected:hover': { bgcolor: `${NAVY}22` },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ color: NAVY }}>All Receipts</Typography>
                      <Chip label={data.receipts.length} size="small"
                        sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: NAVY, color: '#fff' }} />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: GREEN, fontWeight: 700, fontFamily: 'monospace' }}>
                      GH₵ {fmt(data.total)}
                    </Typography>
                  }
                />
              </ListItemButton>

              {groups.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="caption">No receipts in this period.</Typography>
                </Box>
              ) : (
                groups.map(g => {
                  const isChecked = selected.has(g.name);
                  return (
                    <ListItemButton
                      key={g.name}
                      selected={isChecked}
                      onClick={() => toggleGroup(g.name)}
                      sx={{
                        borderBottom: '1px solid #eef0f4', pl: 1,
                        '&.Mui-selected': { bgcolor: `${NAVY}15`, borderLeft: `4px solid ${NAVY}` },
                        '&.Mui-selected:hover': { bgcolor: `${NAVY}22` },
                      }}
                    >
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={isChecked}
                        tabIndex={-1}
                        disableRipple
                        sx={{ p: 0.5, color: NAVY, '&.Mui-checked': { color: NAVY } }}
                      />
                      <ListItemText
                        sx={{ my: 0 }}
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={700} sx={{
                              color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {g.name}
                            </Typography>
                            <Chip label={g.count} size="small"
                              sx={{ height: 20, fontSize: 11, fontWeight: 700,
                                bgcolor: isChecked ? NAVY : `${NAVY}20`,
                                color:   isChecked ? '#fff' : NAVY }} />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: GREEN, fontWeight: 700, fontFamily: 'monospace' }}>
                            GH₵ {fmt(g.total)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Paper>
        </Grid>

        {/* Right column: Statement paper */}
        <Grid item xs={12} md={8} lg={9}>
      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 2, md: 4 }, bgcolor: '#fff',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            pb: 2, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 84, height: 84, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 28,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3, mb: 0.5 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              {reportTitle}
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
            {filterLabel && (
              <Typography className="filter-line" variant="caption" sx={{ display: 'block', mt: 0.5, color: '#444' }}>
                Filtered by {groupSingular}: <b>{filterLabel}</b>
              </Typography>
            )}
          </Box>

          {/* Table */}
          {filtered.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Receipt sx={{ fontSize: 40, color: '#c9d3e0', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={700}>No receipts to display</Typography>
              <Typography variant="body2">
                {data.receipts.length === 0
                  ? 'There are no receipts in this period.'
                  : 'No receipts match your filter.'}
              </Typography>
            </Box>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 36 }}>#</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Receipt No.</th>
                  <th style={thStyle}>Income Type</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Method</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount (GH₵)</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id} style={{ background: i % 2 === 1 ? '#fafbfc' : 'transparent' }}>
                    <td style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                    <td style={cellStyle}>{fmtDateShort(r.income_date)}</td>
                    <td style={{ ...cellStyle, fontWeight: 700, color: NAVY }}>{r.receipt_no || '—'}</td>
                    <td style={cellStyle}>{r.income_type_name || '—'}</td>
                    <td style={cellStyle}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        background: `${NAVY}12`, color: NAVY,
                        fontSize: 11, fontWeight: 700,
                      }}>{r.income_category || '—'}</span>
                    </td>
                    <td style={{ ...cellStyle, color: '#475569', maxWidth: 280 }}>{r.narration || '—'}</td>
                    <td style={cellStyle}>
                      <span style={methodChip(r.payment_method)}>
                        {METHOD_LABEL[r.payment_method] || r.payment_method || '—'}
                      </span>
                    </td>
                    <td style={amountStyle}>{fmt(r.amount)}</td>
                  </tr>
                ))}
                <tr className="grand">
                  <td colSpan={7} style={grandLabelStyle}>
                    TOTAL ({filtered.length} receipt{filtered.length === 1 ? '' : 's'})
                  </td>
                  <td style={grandAmountStyle}>{fmt(filteredTotal)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 4,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '9px 10px', fontWeight: 700,
};
const cellStyle    = { padding: '6px 10px', fontSize: 12.5, borderBottom: '1px solid #eef0f4', color: '#1e293b' };
const amountStyle  = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                       fontWeight: 700, color: '#0F766E' };
const grandLabelStyle = {
  padding: '12px 10px', fontSize: 13, fontWeight: 800, background: '#fffdf5',
  color: GREEN, borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  textTransform: 'uppercase', letterSpacing: '0.08em',
};
const grandAmountStyle = {
  padding: '12px 10px', fontSize: 14, fontWeight: 800, textAlign: 'right',
  background: '#fffdf5', color: GREEN,
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const methodChip = (method) => {
  const map = {
    cash:         { bg: '#e7f5ef', fg: '#0F766E' },
    cheque:       { bg: '#eef2f7', fg: NAVY },
    transfer:     { bg: '#e6f0fb', fg: '#1565C0' },
    mobile_money: { bg: '#fff4de', fg: '#8a5a08' },
  };
  const c = map[method] || { bg: '#f1f5f9', fg: '#475569' };
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
    background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700,
  };
};
